// Localização: frontend/src/utils/gerarNotasClinicasPDF.js
// Função utilitária para gerar PDF de notas clínicas (individual ou agrupado)

import jsPDF from 'jspdf';
import { PDFDocument, rgb } from 'pdf-lib';
import { getNotasClinicas, getProntuarioSummary, downloadDocumentoPaciente } from '../api/api';
import { getUsuarioFotoUrl } from './imageUtils';

/**
 * Normaliza texto para garantir compatibilidade com jsPDF
 * Substitui caracteres especiais e símbolos Unicode por versões compatíveis
 * @param {string} texto - Texto original
 * @returns {string} Texto normalizado
 */
const normalizarTextoPDF = (texto) => {
    if (!texto || typeof texto !== 'string') return texto;
    
    let textoNormalizado = texto;
    
    // Substituir caracteres subscritos (Unicode subscripts) - mais comum em textos médicos
    // O₂ (U+2082), CO₂, H₂O, etc.
    textoNormalizado = textoNormalizado
        .replace(/O\u2082/g, 'O2')  // O₂ -> O2
        .replace(/CO\u2082/g, 'CO2') // CO₂ -> CO2
        .replace(/H\u2082O/g, 'H2O') // H₂O -> H2O
        .replace(/\u2082/g, '2')      // Qualquer subscrito 2
        .replace(/\u2080/g, '0')      // Qualquer subscrito 0
        .replace(/\u2081/g, '1')      // Qualquer subscrito 1
        .replace(/\u2083/g, '3')      // Qualquer subscrito 3
        .replace(/\u2084/g, '4')      // Qualquer subscrito 4
        .replace(/\u2085/g, '5')      // Qualquer subscrito 5
        .replace(/\u2086/g, '6')      // Qualquer subscrito 6
        .replace(/\u2087/g, '7')      // Qualquer subscrito 7
        .replace(/\u2088/g, '8')      // Qualquer subscrito 8
        .replace(/\u2089/g, '9');     // Qualquer subscrito 9
    
    // Mapa de substituições para outros caracteres especiais e símbolos
    const substituicoes = {
        // Símbolos de temperatura
        '°C': ' graus Celsius',
        '°F': ' graus Fahrenheit',
        '°': ' graus',
        // Símbolos matemáticos e médicos
        '±': '+/-',
        '≤': '<=',
        '≥': '>=',
        '→': '->',
        '←': '<-',
        '×': 'x',
        '÷': '/',
        '≈': '~=',
        // Aspas tipográficas (usando Unicode escapes para evitar erros de sintaxe)
        '\u201C': '"', // Aspas duplas abertas
        '\u201D': '"', // Aspas duplas fechadas
        '\u2018': "'", // Aspas simples abertas
        '\u2019': "'", // Aspas simples fechadas
        // Travessões
        '–': '-',
        '—': '-',
        // Reticências
        '…': '...',
        // Outros símbolos Unicode problemáticos
        '\u200B': '', // Zero-width space
        '\u200C': '', // Zero-width non-joiner
        '\u200D': '', // Zero-width joiner
        '\uFEFF': '', // Zero-width no-break space
    };
    
    // Aplicar substituições
    Object.entries(substituicoes).forEach(([original, substituicao]) => {
        textoNormalizado = textoNormalizado.replace(new RegExp(original.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), substituicao);
    });
    
    // Normalizar espaços e caracteres de controle
    textoNormalizado = textoNormalizado
        .replace(/\s+/g, ' ') // Múltiplos espaços em um
        .replace(/\u00A0/g, ' ') // Espaços não-quebráveis em espaços normais
        .replace(/[\u2000-\u200F]/g, ' ') // Vários tipos de espaços Unicode
        .replace(/[\u2028-\u202F]/g, ' ') // Mais espaços Unicode
        .trim();
    
    return textoNormalizado;
};

/**
 * Normaliza URL do Cloudinary para garantir uso de /image/upload/ para PNGs
 * e adiciona transformações para garantir formato PNG
 * @param {string} url - URL original do Cloudinary
 * @returns {string} URL normalizada
 */
const normalizarUrlCloudinary = (url) => {
    if (!url || typeof url !== 'string') return url;
    
    const urlLower = url.toLowerCase();
    if (!urlLower.includes('cloudinary.com')) return url;
    
    // Se a URL contém /raw/upload/, substituir por /image/upload/
    let urlNormalizada = url;
    if (url.includes('/raw/upload/')) {
        urlNormalizada = url.replace('/raw/upload/', '/image/upload/');
    }
    
    // Remover extensão .png se existir (o Cloudinary não precisa dela no path)
    urlNormalizada = urlNormalizada.replace(/\.png(\?|$)/i, '$1');
    
    // Separar URL base e query parameters
    const urlParts = urlNormalizada.split('?');
    const baseUrl = urlParts[0];
    const existingParams = urlParts[1] || '';
    
    // Adicionar transformação f_png como query parameter para forçar formato PNG
    // e cache-busting para garantir versão mais recente
    const params = new URLSearchParams(existingParams);
    params.set('f', 'png'); // Forçar formato PNG
    params.set('_t', Date.now().toString()); // Cache-busting
    
    // Construir URL final
    urlNormalizada = `${baseUrl}?${params.toString()}`;
    
    return urlNormalizada;
};

/**
 * Gera um PDF de uma nota clínica individual com assinatura
 * @param {number} pacienteId - ID do paciente
 * @param {object} nota - Objeto da nota clínica
 */
export const gerarNotaClinicaPDF = async (pacienteId, nota) => {
    try {
        const [summaryResponse] = await Promise.all([
            getProntuarioSummary(pacienteId).catch(() => ({ data: { data: { paciente: {} } } }))
        ]);

        const paciente = summaryResponse.data.data.paciente;

        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 20;
        let yPos = margin;

        // Cores
        const corEmerald = [16, 185, 129];
        const corGray = [107, 114, 128];

        // Cabeçalho
        doc.setFillColor(...corEmerald);
        doc.rect(0, 0, pageWidth, 40, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(18);
        doc.setFont(undefined, 'bold');
        doc.text('Nota Clínica', margin, 25);

        yPos = 50;

        // Informações do paciente
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('Paciente:', margin, yPos);
        doc.setFont(undefined, 'normal');
        doc.text(normalizarTextoPDF(paciente.nome_completo || 'Não informado'), margin + 30, yPos);
        yPos += 8;

        if (paciente.data_nascimento) {
            doc.setFont(undefined, 'bold');
            doc.text('Data de Nascimento:', margin, yPos);
            doc.setFont(undefined, 'normal');
            doc.text(new Date(paciente.data_nascimento).toLocaleDateString('pt-BR'), margin + 50, yPos);
            yPos += 8;
        }

        yPos += 5;

        // Tipo e data da nota
        doc.setFont(undefined, 'bold');
        doc.text('Tipo:', margin, yPos);
        doc.setFont(undefined, 'normal');
        const tipoLabel = nota.tipo === 'evolucao' ? 'Evolução' : 
                          nota.tipo === 'intercorrencia' ? 'Intercorrência' : 'Observação';
        doc.text(normalizarTextoPDF(tipoLabel), margin + 20, yPos);
        yPos += 8;

        doc.setFont(undefined, 'bold');
        doc.text('Data:', margin, yPos);
        doc.setFont(undefined, 'normal');
        doc.text(new Date(nota.data_registro).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }), margin + 20, yPos);
        yPos += 8;

        if (nota.titulo) {
            doc.setFont(undefined, 'bold');
            doc.text('Título:', margin, yPos);
            doc.setFont(undefined, 'normal');
            doc.text(normalizarTextoPDF(nota.titulo), margin + 25, yPos);
            yPos += 8;
        }

        yPos += 5;

        // Conteúdo
        doc.setFont(undefined, 'bold');
        doc.text('Conteúdo:', margin, yPos);
        yPos += 8;

        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);
        const conteudoNormalizado = normalizarTextoPDF(nota.conteudo || 'Sem conteúdo');
        
        // Função auxiliar para justificar texto manualmente
        const adicionarTextoJustificado = (texto, x, y, maxWidth) => {
            const palavras = texto.trim().split(/\s+/);
            if (palavras.length === 0) return y;
            
            const fontSize = doc.getFontSize();
            let linhaAtual = [];
            let larguraLinhaAtual = 0;
            const espacoLargura = doc.getTextWidth(' ');
            
            palavras.forEach((palavra, index) => {
                const larguraPalavra = doc.getTextWidth(palavra);
                const larguraComEspaco = larguraLinhaAtual + (linhaAtual.length > 0 ? espacoLargura : 0) + larguraPalavra;
                
                // Se a palavra sozinha é maior que maxWidth, força quebra
                if (larguraPalavra > maxWidth) {
                    // Desenha linha anterior se houver
                    if (linhaAtual.length > 0) {
                        const textoLinha = linhaAtual.join(' ');
                        const larguraTexto = doc.getTextWidth(textoLinha);
                        const espacoDisponivel = maxWidth - larguraTexto;
                        const numEspacos = linhaAtual.length - 1;
                        
                        if (numEspacos > 0 && espacoDisponivel > 0 && index < palavras.length) {
                            // Justificar: distribuir espaço extra entre palavras
                            const espacoExtraPorPalavra = espacoDisponivel / numEspacos;
                            let xAtual = x;
                            
                            linhaAtual.forEach((pal, idx) => {
                                doc.text(pal, xAtual, y);
                                if (idx < linhaAtual.length - 1) {
                                    const larguraPal = doc.getTextWidth(pal);
                                    xAtual += larguraPal + espacoLargura + espacoExtraPorPalavra;
                                }
                            });
                        } else {
                            // Linha curta - alinhar à esquerda
                            doc.text(textoLinha, x, y);
                        }
                        
                        y += 6;
                        if (y > pageHeight - 60) {
                            doc.addPage();
                            y = margin;
                        }
                    }
                    
                    // Quebra palavra muito longa (não ideal, mas necessário)
                    const chars = palavra.split('');
                    let palavraParte = '';
                    chars.forEach(char => {
                        const teste = palavraParte + char;
                        if (doc.getTextWidth(teste) <= maxWidth) {
                            palavraParte = teste;
                        } else {
                            if (palavraParte) {
                                linhaAtual = [palavraParte];
                                larguraLinhaAtual = doc.getTextWidth(palavraParte);
                                palavraParte = char;
                            }
                        }
                    });
                    linhaAtual = palavraParte ? [palavraParte] : [];
                    larguraLinhaAtual = palavraParte ? doc.getTextWidth(palavraParte) : 0;
                } else if (larguraComEspaco <= maxWidth) {
                    // Adiciona palavra à linha atual
                    linhaAtual.push(palavra);
                    larguraLinhaAtual = larguraComEspaco;
                } else {
                    // Desenha linha anterior se houver
                    if (linhaAtual.length > 0) {
                        const textoLinha = linhaAtual.join(' ');
                        const larguraTexto = doc.getTextWidth(textoLinha);
                        const espacoDisponivel = maxWidth - larguraTexto;
                        const numEspacos = linhaAtual.length - 1;
                        
                        if (numEspacos > 0 && espacoDisponivel > 0 && index < palavras.length) {
                            // Justificar: distribuir espaço extra entre palavras
                            const espacoExtraPorPalavra = espacoDisponivel / numEspacos;
                            let xAtual = x;
                            
                            linhaAtual.forEach((pal, idx) => {
                                doc.text(pal, xAtual, y);
                                if (idx < linhaAtual.length - 1) {
                                    const larguraPal = doc.getTextWidth(pal);
                                    xAtual += larguraPal + espacoLargura + espacoExtraPorPalavra;
                                }
                            });
                        } else {
                            // Linha curta - alinhar à esquerda
                            doc.text(textoLinha, x, y);
                        }
                        
                        y += 6;
                        if (y > pageHeight - 60) {
                            doc.addPage();
                            y = margin;
                        }
                    }
                    
                    // Inicia nova linha
                    linhaAtual = [palavra];
                    larguraLinhaAtual = larguraPalavra;
                }
            });
            
            // Desenha última linha (sem justificação)
            if (linhaAtual.length > 0) {
                doc.text(linhaAtual.join(' '), x, y);
                y += 6;
            }
            
            return y;
        };
        
        const maxWidth = pageWidth - (margin * 2);
        yPos = adicionarTextoJustificado(conteudoNormalizado, margin, yPos, maxWidth);

        yPos += 10;

        // Informações da assinatura para incluir depois
        // IMPORTANTE: Usar APENAS a assinatura salva na nota (nota.assinatura_url)
        // Não usar assinatura do usuário atual, pois a nota foi assinada por outro usuário
        let assinaturaInfo = null;
        if (nota.assinado && nota.assinatura_url) {
            // Usar APENAS a URL da assinatura salva na nota (do usuário que assinou)
            assinaturaInfo = {
                url: nota.assinatura_url,
                usuarioNome: nota.usuario_assinante_nome,
                dataAssinatura: nota.data_assinatura
            };
        }

        // Adicionar seção de assinatura no PDF base
        let assinaturaImageData = null;
        if (assinaturaInfo) {
            if (yPos > pageHeight - 80) {
                doc.addPage();
                yPos = margin;
            }

            doc.setFont(undefined, 'bold');
            doc.setFontSize(10);
            doc.text('Assinatura Digital:', margin, yPos);
            yPos += 8;

            // Carregar imagem da assinatura (PNG)
            try {
                if (!assinaturaInfo.url) {
                    throw new Error('URL da assinatura não disponível');
                }

                // IMPORTANTE: Usar APENAS a assinatura salva na nota
                // Não usar assinatura do perfil do usuário atual como fallback
                let assinaturaUrlCompleta;
                const urlLower = String(assinaturaInfo.url).toLowerCase();
                // Verificar se é URL completa (Cloudinary ou outra URL externa)
                const isCloudinaryUrl = urlLower.includes('cloudinary') || 
                                       urlLower.startsWith('http://') || 
                                       urlLower.startsWith('https://');
                
                // Se a URL original é /raw/upload/, tentar primeiro a URL original (pode ser PDF antigo)
                // Depois tentar /image/upload/ e finalmente usar assinatura do perfil
                const isRawUpload = assinaturaInfo.url.includes('/raw/upload/');
                
                if (isCloudinaryUrl && urlLower.includes('cloudinary')) {
                    // Se for /raw/upload/, tentar primeiro a URL original (pode funcionar se for realmente um arquivo raw)
                    if (isRawUpload) {
                        // Tentar URL original primeiro
                        const urlBase = assinaturaInfo.url;
                        const separator = urlBase.includes('?') ? '&' : '?';
                        assinaturaUrlCompleta = `${urlBase}${separator}_t=${Date.now()}`;
                    } else {
                        // Se não for /raw/upload/, normalizar normalmente
                        assinaturaUrlCompleta = normalizarUrlCloudinary(assinaturaInfo.url);
                    }
                } else if (isCloudinaryUrl) {
                    // URL externa mas não Cloudinary
                    const urlBase = assinaturaInfo.url;
                    const separator = urlBase.includes('?') ? '&' : '?';
                    assinaturaUrlCompleta = `${urlBase}${separator}_t=${Date.now()}`;
                } else {
                    assinaturaUrlCompleta = getUsuarioFotoUrl(assinaturaInfo.url);
                }

                // Usar fetch para baixar a imagem como blob (evita problemas de CORS)
                const fetchOptions = {
                    method: 'GET',
                    mode: 'cors',
                    cache: 'no-cache',
                    credentials: 'omit'
                };
                
                if (!isCloudinaryUrl) {
                    const token = localStorage.getItem('token');
                    if (token) {
                        fetchOptions.headers = {
                            'Authorization': `Bearer ${token}`
                        };
                    }
                } else {
                    fetchOptions.referrerPolicy = 'no-referrer';
                }

                let response = await fetch(assinaturaUrlCompleta, fetchOptions);
                
                // Se retornar 404, tentar alternativas
                if (!response.ok && response.status === 404 && isCloudinaryUrl && urlLower.includes('cloudinary')) {
                    const isRawUpload = assinaturaInfo.url.includes('/raw/upload/');
                    
                    // Estratégia 1: Se era /raw/upload/, tentar /image/upload/
                    if (isRawUpload) {
                        let urlAlternativa = assinaturaInfo.url.replace('/raw/upload/', '/image/upload/');
                        urlAlternativa = urlAlternativa.replace(/\.png(\?|$)/i, '$1');
                        const separator = urlAlternativa.includes('?') ? '&' : '?';
                        const urlAlternativaComCache = `${urlAlternativa}${separator}_t=${Date.now()}`;
                        
                        response = await fetch(urlAlternativaComCache, fetchOptions);
                        
                        if (response.ok) {
                            assinaturaUrlCompleta = urlAlternativaComCache;
                        }
                    }
                    
                    // Estratégia 2: Se ainda não funcionou, tentar URL normalizada
                    if (!response.ok && !isRawUpload) {
                        const urlNormalizada = normalizarUrlCloudinary(assinaturaInfo.url);
                        response = await fetch(urlNormalizada, fetchOptions);
                        
                        if (response.ok) {
                            assinaturaUrlCompleta = urlNormalizada;
                        }
                    }
                }
                
                // Se ainda não funcionou, não usar assinatura do perfil do usuário atual
                // A assinatura deve ser a do usuário que assinou a nota
                if (!response.ok) {
                    const errorText = await response.text().catch(() => 'Sem detalhes');
                    throw new Error(`Erro ao baixar assinatura da nota: ${response.status} ${response.statusText}. Detalhes: ${errorText}`);
                }

                let blob = await response.blob();
                
                if (blob.size === 0) {
                    throw new Error('Imagem vazia recebida do servidor');
                }

                // Verificar magic bytes para determinar o tipo real do arquivo
                let arrayBuffer = await blob.arrayBuffer();
                let uint8Array = new Uint8Array(arrayBuffer);
                let magicBytes = Array.from(uint8Array.slice(0, 8));
                
                // Magic bytes conhecidos:
                // PNG: [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A]
                // JPEG: [0xFF, 0xD8, 0xFF]
                // PDF: [0x25, 0x50, 0x44, 0x46] = "%PDF"
                
                let isPNG = magicBytes[0] === 0x89 && magicBytes[1] === 0x50 && magicBytes[2] === 0x4E && magicBytes[3] === 0x47;
                let isJPEG = magicBytes[0] === 0xFF && magicBytes[1] === 0xD8 && magicBytes[2] === 0xFF;
                let isPDF = magicBytes[0] === 0x25 && magicBytes[1] === 0x50 && magicBytes[2] === 0x44 && magicBytes[3] === 0x46;

                // Se for PDF, tentar converter para PNG usando transformação do Cloudinary
                if (isPDF) {
                    const urlLower = String(assinaturaInfo.url).toLowerCase();
                    const isCloudinaryUrl = urlLower.includes('cloudinary');
                    
                    if (isCloudinaryUrl) {
                        // Tentar forçar conversão para PNG usando transformação do Cloudinary
                        // Cloudinary pode converter PDF para PNG usando transformações
                        try {
                            let urlParaConversao = assinaturaInfo.url;
                            
                            // Se já tem transformações, adicionar f=png
                            // Se não tem, adicionar transformação básica
                            if (urlParaConversao.includes('/image/upload/')) {
                                // Já é /image/upload/, adicionar f=png
                                const separator = urlParaConversao.includes('?') ? '&' : '?';
                                urlParaConversao = `${urlParaConversao}${separator}f=png&_t=${Date.now()}`;
                            } else if (urlParaConversao.includes('/raw/upload/')) {
                                // Converter /raw/upload/ para /image/upload/ e adicionar f=png
                                urlParaConversao = urlParaConversao.replace('/raw/upload/', '/image/upload/');
                                const separator = urlParaConversao.includes('?') ? '&' : '?';
                                urlParaConversao = `${urlParaConversao}${separator}f=png&_t=${Date.now()}`;
                            } else {
                                // Tentar adicionar transformação básica
                                const separator = urlParaConversao.includes('?') ? '&' : '?';
                                urlParaConversao = `${urlParaConversao}${separator}f=png&_t=${Date.now()}`;
                            }
                            
                            // Tentar buscar a versão PNG
                            const responsePNG = await fetch(urlParaConversao, fetchOptions);
                            
                            if (responsePNG.ok) {
                                blob = await responsePNG.blob();
                                arrayBuffer = await blob.arrayBuffer();
                                uint8Array = new Uint8Array(arrayBuffer);
                                magicBytes = Array.from(uint8Array.slice(0, 4));
                                
                                // Verificar se agora é PNG
                                isPNG = magicBytes[0] === 0x89 && magicBytes[1] === 0x50 && magicBytes[2] === 0x4E && magicBytes[3] === 0x47;
                                isPDF = false;
                                
                                if (isPNG) {
                                    console.warn('[gerarNotaClinicaPDF] Assinatura PDF convertida para PNG com sucesso via Cloudinary');
                                } else {
                                    // Ainda não é PNG, não podemos usar
                                    throw new Error('Conversão de PDF para PNG não foi bem-sucedida');
                                }
                            } else {
                                throw new Error('Não foi possível converter PDF para PNG');
                            }
                        } catch (conversionError) {
                            // Se a conversão falhar, apenas logar e continuar sem a assinatura
                            console.warn('[gerarNotaClinicaPDF] Não foi possível converter assinatura PDF para PNG. A assinatura não será exibida no PDF.', conversionError);
                            // Não lançar erro, apenas não exibir a assinatura
                            assinaturaImageData = null;
                            throw new Error('ASSINATURA_PDF_SKIP'); // Erro especial para pular a assinatura
                        }
                    } else {
                        // Não é Cloudinary, não podemos converter
                        console.warn('[gerarNotaClinicaPDF] A assinatura salva na nota é um arquivo PDF e não pode ser convertida para PNG. A assinatura não será exibida no PDF.');
                        // Não lançar erro, apenas não exibir a assinatura
                        assinaturaImageData = null;
                        throw new Error('ASSINATURA_PDF_SKIP'); // Erro especial para pular a assinatura
                    }
                }

                // Criar blob com tipo correto baseado nos magic bytes
                let blobParaUsar = blob;
                if (isPNG) {
                    blobParaUsar = new Blob([blob], { type: 'image/png' });
                } else if (isJPEG) {
                    blobParaUsar = new Blob([blob], { type: 'image/jpeg' });
                } else {
                    // Tentar como PNG por padrão
                    blobParaUsar = new Blob([blob], { type: 'image/png' });
                }

                // Converter para data URL usando Canvas para garantir que seja uma imagem válida
                const imgDataUrl = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = async () => {
                        try {
                            const dataUrl = reader.result;
                            
                            // Verificar se o data URL é válido
                            if (!dataUrl.startsWith('data:image/')) {
                                // Extrair apenas o base64
                                const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl.replace(/^data:.*,/, '');
                                const correctedDataUrl = `data:image/png;base64,${base64}`;
                                
                                // Validar usando Canvas
                                const img = new Image();
                                await new Promise((imgResolve, imgReject) => {
                                    const timeout = setTimeout(() => imgReject(new Error('Timeout')), 5000);
                                    img.onload = () => {
                                        clearTimeout(timeout);
                                        imgResolve();
                                    };
                                    img.onerror = () => {
                                        clearTimeout(timeout);
                                        imgReject(new Error('Imagem inválida'));
                                    };
                                    img.src = correctedDataUrl;
                                });
                                
                                resolve(correctedDataUrl);
                            } else {
                                // Validar usando Canvas
                                const img = new Image();
                                await new Promise((imgResolve, imgReject) => {
                                    const timeout = setTimeout(() => imgReject(new Error('Timeout')), 5000);
                                    img.onload = () => {
                                        clearTimeout(timeout);
                                        imgResolve();
                                    };
                                    img.onerror = () => {
                                        clearTimeout(timeout);
                                        imgReject(new Error('Imagem inválida'));
                                    };
                                    img.src = dataUrl;
                                });
                                
                                resolve(dataUrl);
                            }
                        } catch (error) {
                            console.error('[gerarNotaClinicaPDF] Erro ao validar imagem:', error);
                            reject(new Error('Arquivo não é uma imagem válida'));
                        }
                    };
                    reader.onerror = (err) => {
                        console.error('[gerarNotaClinicaPDF] Erro no FileReader:', err);
                        reject(new Error('Erro ao converter blob para data URL'));
                    };
                    reader.readAsDataURL(blobParaUsar);
                });

                // Carregar imagem para obter dimensões
                const img = new Image();
                await new Promise((resolve, reject) => {
                    const timeout = setTimeout(() => {
                        reject(new Error('Timeout ao carregar imagem (10s)'));
                    }, 10000);
                    
                    img.onload = () => {
                        clearTimeout(timeout);
                        resolve();
                    };
                    img.onerror = () => {
                        clearTimeout(timeout);
                        reject(new Error('Erro ao carregar imagem: formato inválido ou corrompido'));
                    };
                    img.src = imgDataUrl;
                });

                if (!img.width || !img.height) {
                    throw new Error('Imagem carregada mas sem dimensões válidas');
                }

                // Calcular dimensões mantendo proporção (largura máxima: 60mm)
                const maxWidth = 60; // mm
                const aspectRatio = img.height / img.width;
                let imgWidth = maxWidth;
                let imgHeight = maxWidth * aspectRatio;
                
                // Limitar altura máxima a 30mm
                if (imgHeight > 30) {
                    imgHeight = 30;
                    imgWidth = 30 / aspectRatio;
                }

                // Verificar se precisa de nova página
                if (yPos + imgHeight > pageHeight - 40) {
                    doc.addPage();
                    yPos = margin;
                }

                // Adicionar imagem da assinatura usando data URL
                try {
                    doc.addImage(imgDataUrl, 'PNG', margin, yPos, imgWidth, imgHeight);
                    yPos += imgHeight + 5;
                } catch (addImageError) {
                    console.error('[gerarNotaClinicaPDF] Erro ao adicionar imagem ao PDF:', addImageError);
                    throw new Error(`Erro ao adicionar imagem ao PDF: ${addImageError.message}`);
                }

            } catch (error) {
                // Se for erro especial de PDF, apenas pular a assinatura sem logar erro crítico
                if (error.message === 'ASSINATURA_PDF_SKIP') {
                    // Já foi logado um aviso acima, apenas continuar sem exibir mensagem
                    // Não adicionar mensagem de fallback para PDFs
                } else {
                    console.error('[gerarNotaClinicaPDF] Erro ao carregar imagem da assinatura:', error);
                    // Adicionar mensagem de fallback apenas para outros erros
                    doc.setFont(undefined, 'normal');
                    doc.setFontSize(8);
                    doc.setTextColor(...corGray);
                    doc.text('Assinatura digital registrada (imagem não disponível)', margin, yPos);
                    yPos += 5;
                }
            }

            if (assinaturaInfo.usuarioNome) {
                doc.setFont(undefined, 'normal');
                doc.setFontSize(9);
                doc.text(normalizarTextoPDF(assinaturaInfo.usuarioNome || ''), margin, yPos);
                yPos += 5;
            }

            if (assinaturaInfo.dataAssinatura) {
                doc.setFontSize(8);
                doc.setTextColor(...corGray);
                doc.text(
                    `Assinado em: ${new Date(assinaturaInfo.dataAssinatura).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    })}`,
                    margin,
                    yPos
                );
            }
        }

        // Gerar PDF base como array de bytes
        const pdfBaseBytes = doc.output('arraybuffer');

        // Criar documento PDF principal para mesclar documentos anexados
        let pdfDoc = await PDFDocument.load(pdfBaseBytes);

        // Mesclar documentos PDF anexados, se houver
        if (nota.documentos && Array.isArray(nota.documentos) && nota.documentos.length > 0) {
            try {
                // Para cada documento anexado, baixar e mesclar
                for (const documento of nota.documentos) {
                    try {
                        const response = await downloadDocumentoPaciente(pacienteId, documento.id);
                        // response.data já é um Blob quando responseType é 'blob'
                        const documentoPdfBytes = await response.data.arrayBuffer();
                        const documentoPdf = await PDFDocument.load(documentoPdfBytes);
                        
                        // Copiar todas as páginas do documento para o PDF principal
                        const paginas = await pdfDoc.copyPages(documentoPdf, documentoPdf.getPageIndices());
                        paginas.forEach((pagina) => {
                            pdfDoc.addPage(pagina);
                        });
                    } catch (error) {
                        console.error(`Erro ao incluir documento ${documento.id} (${documento.titulo}):`, error);
                        // Continuar mesmo se houver erro em um documento
                    }
                }
            } catch (error) {
                console.error('Erro ao mesclar documentos PDF:', error);
            }
        }

        // Adicionar rodapé em todas as páginas usando pdf-lib
        const totalPagesFinal = pdfDoc.getPageCount();
        const helveticaFont = await pdfDoc.embedFont('Helvetica');
        
        // Data e hora de geração
        const dataHoraGeracao = new Date();
        const dataFormatada = dataHoraGeracao.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
        const horaFormatada = dataHoraGeracao.toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit'
        });
        
        for (let i = 0; i < totalPagesFinal; i++) {
            const page = pdfDoc.getPage(i);
            const { width } = page.getSize();
            
            // Texto do rodapé com número de página e data/hora
            const textoRodape = `Página ${i + 1} de ${totalPagesFinal} | Gerado em ${dataFormatada} às ${horaFormatada} pela Higeia IA`;
            
            // Calcular largura do texto para centralizar
            const textWidth = helveticaFont.widthOfTextAtSize(textoRodape, 8);
            const xPos = (width - textWidth) / 2;
            
            // Adicionar texto do rodapé centralizado
            page.drawText(textoRodape, {
                x: xPos,
                y: 20,
                size: 8,
                color: rgb(0.5, 0.5, 0.5), // Cinza
                font: helveticaFont,
            });
        }

        // Salvar PDF final
        const pdfFinal = await pdfDoc.save();

        // Fazer download do PDF final
        const blob = new Blob([pdfFinal], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Nota_Clinica_${nota.id}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

    } catch (error) {
        console.error('Erro ao gerar PDF da nota clínica:', error);
        throw new Error('Erro ao gerar o PDF da nota clínica. Por favor, tente novamente.');
    }
};

/**
 * Gera um PDF com múltiplas notas clínicas agrupadas
 * @param {number} pacienteId - ID do paciente
 * @param {Array} notas - Array de notas clínicas
 * @param {object} options - Opções { incluirAssinatura: boolean }
 */
export const gerarNotasClinicasAgrupadasPDF = async (pacienteId, notas, options = {}) => {
    try {
        const { incluirAssinatura = true } = options;

        const [summaryResponse] = await Promise.all([
            getProntuarioSummary(pacienteId).catch(() => ({ data: { data: { paciente: {} } } }))
        ]);

        const paciente = summaryResponse.data.data.paciente;

        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 20;
        let yPos = margin;

        // Cores
        const corEmerald = [16, 185, 129];
        const corGray = [107, 114, 128];

        // Cabeçalho
        doc.setFillColor(...corEmerald);
        doc.rect(0, 0, pageWidth, 40, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(18);
        doc.setFont(undefined, 'bold');
        doc.text('Notas Clínicas', margin, 25);

        yPos = 50;

        // Informações do paciente
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('Paciente:', margin, yPos);
        doc.setFont(undefined, 'normal');
        doc.text(normalizarTextoPDF(paciente.nome_completo || 'Não informado'), margin + 30, yPos);
        yPos += 8;

        if (paciente.data_nascimento) {
            doc.setFont(undefined, 'bold');
            doc.text('Data de Nascimento:', margin, yPos);
            doc.setFont(undefined, 'normal');
            doc.text(new Date(paciente.data_nascimento).toLocaleDateString('pt-BR'), margin + 50, yPos);
            yPos += 8;
        }

        yPos += 10;

        // Array para armazenar informações das assinaturas individuais
        const assinaturasIndividuais = [];

        // Notas - usando for...of para suportar async/await
        for (let index = 0; index < notas.length; index++) {
            const nota = notas[index];
            
            // Rastrear página atual antes de adicionar conteúdo
            const paginaInicial = doc.internal.getCurrentPageInfo().pageNumber;
            
            if (yPos > pageHeight - 100) {
                doc.addPage();
                yPos = margin;
            }

            // Separador
            if (index > 0) {
                doc.setDrawColor(...corGray);
                doc.setLineWidth(0.5);
                doc.line(margin, yPos, pageWidth - margin, yPos);
                yPos += 10;
            }

            // Tipo e data
            doc.setFont(undefined, 'bold');
            doc.setFontSize(11);
            const tipoLabel = nota.tipo === 'evolucao' ? 'Evolução' : 
                              nota.tipo === 'intercorrencia' ? 'Intercorrência' : 'Observação';
            doc.text(normalizarTextoPDF(`${tipoLabel} - ${new Date(nota.data_registro).toLocaleDateString('pt-BR')}`), margin, yPos);
            yPos += 8;

            if (nota.titulo) {
                doc.setFont(undefined, 'bold');
                doc.setFontSize(10);
                doc.text(normalizarTextoPDF(nota.titulo), margin, yPos);
                yPos += 6;
            }

            // Conteúdo
            doc.setFont(undefined, 'normal');
            doc.setFontSize(9);
            const conteudoNormalizado = normalizarTextoPDF(nota.conteudo || 'Sem conteúdo');
            
            // Função auxiliar para justificar texto manualmente
            const adicionarTextoJustificado = (texto, x, y, maxWidth) => {
                const palavras = texto.trim().split(/\s+/);
                if (palavras.length === 0) return y;
                
                let linhaAtual = [];
                let larguraLinhaAtual = 0;
                const espacoLargura = doc.getTextWidth(' ');
                
                palavras.forEach((palavra, index) => {
                    const larguraPalavra = doc.getTextWidth(palavra);
                    const larguraComEspaco = larguraLinhaAtual + (linhaAtual.length > 0 ? espacoLargura : 0) + larguraPalavra;
                    
                    // Se a palavra sozinha é maior que maxWidth, força quebra
                    if (larguraPalavra > maxWidth) {
                        // Desenha linha anterior se houver
                        if (linhaAtual.length > 0) {
                            const textoLinha = linhaAtual.join(' ');
                            const larguraTexto = doc.getTextWidth(textoLinha);
                            const espacoDisponivel = maxWidth - larguraTexto;
                            const numEspacos = linhaAtual.length - 1;
                            
                            if (numEspacos > 0 && espacoDisponivel > 0 && index < palavras.length) {
                                // Justificar: distribuir espaço extra entre palavras
                                const espacoExtraPorPalavra = espacoDisponivel / numEspacos;
                                let xAtual = x;
                                
                                linhaAtual.forEach((pal, idx) => {
                                    doc.text(pal, xAtual, y);
                                    if (idx < linhaAtual.length - 1) {
                                        const larguraPal = doc.getTextWidth(pal);
                                        xAtual += larguraPal + espacoLargura + espacoExtraPorPalavra;
                                    }
                                });
                            } else {
                                // Linha curta - alinhar à esquerda
                                doc.text(textoLinha, x, y);
                            }
                            
                            y += 5;
                            if (y > pageHeight - (incluirAssinatura ? 100 : 40)) {
                                doc.addPage();
                                y = margin;
                            }
                        }
                        
                        // Quebra palavra muito longa (não ideal, mas necessário)
                        const chars = palavra.split('');
                        let palavraParte = '';
                        chars.forEach(char => {
                            const teste = palavraParte + char;
                            if (doc.getTextWidth(teste) <= maxWidth) {
                                palavraParte = teste;
                            } else {
                                if (palavraParte) {
                                    linhaAtual = [palavraParte];
                                    larguraLinhaAtual = doc.getTextWidth(palavraParte);
                                    palavraParte = char;
                                }
                            }
                        });
                        linhaAtual = palavraParte ? [palavraParte] : [];
                        larguraLinhaAtual = palavraParte ? doc.getTextWidth(palavraParte) : 0;
                    } else if (larguraComEspaco <= maxWidth) {
                        // Adiciona palavra à linha atual
                        linhaAtual.push(palavra);
                        larguraLinhaAtual = larguraComEspaco;
                    } else {
                        // Desenha linha anterior se houver
                        if (linhaAtual.length > 0) {
                            const textoLinha = linhaAtual.join(' ');
                            const larguraTexto = doc.getTextWidth(textoLinha);
                            const espacoDisponivel = maxWidth - larguraTexto;
                            const numEspacos = linhaAtual.length - 1;
                            
                            if (numEspacos > 0 && espacoDisponivel > 0 && index < palavras.length) {
                                // Justificar: distribuir espaço extra entre palavras
                                const espacoExtraPorPalavra = espacoDisponivel / numEspacos;
                                let xAtual = x;
                                
                                linhaAtual.forEach((pal, idx) => {
                                    doc.text(pal, xAtual, y);
                                    if (idx < linhaAtual.length - 1) {
                                        const larguraPal = doc.getTextWidth(pal);
                                        xAtual += larguraPal + espacoLargura + espacoExtraPorPalavra;
                                    }
                                });
                            } else {
                                // Linha curta - alinhar à esquerda
                                doc.text(textoLinha, x, y);
                            }
                            
                            y += 5;
                            if (y > pageHeight - (incluirAssinatura ? 100 : 40)) {
                                doc.addPage();
                                y = margin;
                            }
                        }
                        
                        // Inicia nova linha
                        linhaAtual = [palavra];
                        larguraLinhaAtual = larguraPalavra;
                    }
                });
                
                // Desenha última linha (sem justificação)
                if (linhaAtual.length > 0) {
                    doc.text(linhaAtual.join(' '), x, y);
                    y += 5;
                }
                
                return y;
            };
            
            const maxWidth = pageWidth - (margin * 2);
            yPos = adicionarTextoJustificado(conteudoNormalizado, margin, yPos, maxWidth);

            yPos += 5;

            // Assinatura se disponível e solicitada - apenas marcar posição, não adicionar imagem ainda
                if (incluirAssinatura && nota.assinado && nota.assinatura_url) {
                if (yPos > pageHeight - 80) {
                    doc.addPage();
                    yPos = margin;
                }

                // Obter página atual
                const paginaAtual = doc.internal.getCurrentPageInfo().pageNumber;
                
                doc.setFont(undefined, 'bold');
                doc.setFontSize(9);
                doc.text('Assinatura:', margin, yPos);
                
                // Armazenar informações da assinatura para adicionar depois com pdf-lib
                assinaturasIndividuais.push({
                    notaId: nota.id,
                    assinaturaUrl: nota.assinatura_url,
                    pagina: paginaAtual,
                    yPos: yPos, // posição Y em mm
                    usuarioNome: nota.usuario_assinante_nome
                });
                
                yPos += 6;
                
                // Adicionar espaço para a imagem (será preenchido depois)
                const maxImgHeight = 30; // mm
                yPos += maxImgHeight + 3;
                
                if (nota.usuario_assinante_nome) {
                    doc.setFont(undefined, 'normal');
                    doc.setFontSize(8);
                    doc.text(normalizarTextoPDF(nota.usuario_assinante_nome || ''), margin, yPos);
                    yPos += 4;
                }
            }

            yPos += 10;
        }

        // Gerar PDF base como array de bytes
        const pdfBaseBytes = doc.output('arraybuffer');

        // Mesclar documentos PDF anexados de todas as notas e assinatura do perfil
        let pdfFinal;
        const todasNotasComDocumentos = notas.filter(nota => nota.documentos && Array.isArray(nota.documentos) && nota.documentos.length > 0);
        const temDocumentos = todasNotasComDocumentos.length > 0;
        
        // IMPORTANTE: Não buscar assinatura do perfil do usuário atual
        // Cada nota deve usar sua própria assinatura (nota.assinatura_url)
        // que foi salva quando a nota foi assinada pelo usuário correto
        
        // Sempre criar o PDF document se houver documentos ou assinaturas individuais
        if (temDocumentos || assinaturasIndividuais.length > 0) {
            try {
                // Criar documento PDF principal
                const pdfDoc = await PDFDocument.load(pdfBaseBytes);

                // Para cada nota com documentos, mesclar os PDFs
                if (temDocumentos) {
                    for (const nota of todasNotasComDocumentos) {
                        for (const documento of nota.documentos) {
                            try {
                                const response = await downloadDocumentoPaciente(pacienteId, documento.id);
                                // response.data já é um Blob quando responseType é 'blob'
                                const documentoPdfBytes = await response.data.arrayBuffer();
                                const documentoPdf = await PDFDocument.load(documentoPdfBytes);
                                
                                // Copiar todas as páginas do documento para o PDF principal
                                const paginas = await pdfDoc.copyPages(documentoPdf, documentoPdf.getPageIndices());
                                paginas.forEach((pagina) => {
                                    pdfDoc.addPage(pagina);
                                });
                            } catch (error) {
                                console.error(`[gerarNotasClinicasPDF] Erro ao incluir documento ${documento.id} (${documento.titulo}) da nota ${nota.id}:`, error);
                                // Continuar mesmo se houver erro em um documento
                            }
                        }
                    }
                }

                // Adicionar assinaturas individuais usando pdf-lib
                if (assinaturasIndividuais.length > 0) {
                    for (const assinaturaInfo of assinaturasIndividuais) {
                        try {
                            // Ajustar índice da página (pdf-lib usa índice 0-based, jsPDF usa 1-based)
                            const paginaIndex = assinaturaInfo.pagina - 1;
                            
                            // Verificar se a página existe
                            if (paginaIndex < 0 || paginaIndex >= pdfDoc.getPageCount()) {
                                continue;
                            }
                            
                            const pagina = pdfDoc.getPage(paginaIndex);
                            
                            // Construir URL completa da assinatura
                            let assinaturaUrlCompleta;
                            const urlLower = assinaturaInfo.assinaturaUrl.toLowerCase();
                            // Verificar se é URL completa (Cloudinary ou outra URL externa)
                            const isCloudinaryUrl = urlLower.includes('cloudinary') || 
                                                   urlLower.startsWith('http://') || 
                                                   urlLower.startsWith('https://');
                            
                            const isRawUpload = assinaturaInfo.assinaturaUrl.includes('/raw/upload/');
                            
                            if (isCloudinaryUrl && urlLower.includes('cloudinary')) {
                                // Se for /raw/upload/, tentar primeiro a URL original
                                if (isRawUpload) {
                                    const urlBase = assinaturaInfo.assinaturaUrl;
                                    const separator = urlBase.includes('?') ? '&' : '?';
                                    assinaturaUrlCompleta = `${urlBase}${separator}_t=${Date.now()}`;
                                } else {
                                    assinaturaUrlCompleta = normalizarUrlCloudinary(assinaturaInfo.assinaturaUrl);
                                }
                            } else if (isCloudinaryUrl) {
                                const urlBase = assinaturaInfo.assinaturaUrl;
                                const separator = urlBase.includes('?') ? '&' : '?';
                                assinaturaUrlCompleta = `${urlBase}${separator}_t=${Date.now()}`;
                            } else {
                                assinaturaUrlCompleta = getUsuarioFotoUrl(assinaturaInfo.assinaturaUrl);
                            }

                            // Usar fetch para baixar a imagem como blob (evita problemas de CORS)
                            const fetchOptions = {
                                method: 'GET',
                                mode: 'cors',
                                cache: 'no-cache',
                                credentials: 'omit'
                            };
                            
                            if (!isCloudinaryUrl) {
                                const token = localStorage.getItem('token');
                                if (token) {
                                    fetchOptions.headers = {
                                        'Authorization': `Bearer ${token}`
                                    };
                                }
                            } else {
                                fetchOptions.referrerPolicy = 'no-referrer';
                            }

                            let response = await fetch(assinaturaUrlCompleta, fetchOptions);
                            
                            // Se retornar 404, tentar alternativas
                            if (!response.ok && response.status === 404 && isCloudinaryUrl && urlLower.includes('cloudinary')) {
                                // Estratégia 1: Se era /raw/upload/, tentar /image/upload/
                                if (isRawUpload) {
                                    let urlAlternativa = assinaturaInfo.assinaturaUrl.replace('/raw/upload/', '/image/upload/');
                                    urlAlternativa = urlAlternativa.replace(/\.png(\?|$)/i, '$1');
                                    const separator = urlAlternativa.includes('?') ? '&' : '?';
                                    const urlAlternativaComCache = `${urlAlternativa}${separator}_t=${Date.now()}`;
                                    
                                    response = await fetch(urlAlternativaComCache, fetchOptions);
                                    
                                    if (response.ok) {
                                        assinaturaUrlCompleta = urlAlternativaComCache;
                                    }
                                }
                                
                                // Estratégia 2: Se ainda não funcionou, tentar URL normalizada
                                if (!response.ok && !isRawUpload) {
                                    const urlNormalizada = normalizarUrlCloudinary(assinaturaInfo.assinaturaUrl);
                                    response = await fetch(urlNormalizada, fetchOptions);
                                    
                                    if (response.ok) {
                                        assinaturaUrlCompleta = urlNormalizada;
                                    }
                                }
                            }
                            
                            // IMPORTANTE: Não usar assinatura do perfil do usuário atual como fallback
                            // A assinatura deve ser a do usuário que assinou a nota
                            if (!response.ok) {
                                throw new Error(`Erro ao baixar assinatura da nota: ${response.status} ${response.statusText}`);
                            }

                            let blob = await response.blob();
                            
                            // Verificar magic bytes para garantir que é PNG, não PDF
                            let arrayBuffer = await blob.arrayBuffer();
                            let uint8Array = new Uint8Array(arrayBuffer);
                            let magicBytes = Array.from(uint8Array.slice(0, 4));
                            
                            let isPNG = magicBytes[0] === 0x89 && magicBytes[1] === 0x50 && magicBytes[2] === 0x4E && magicBytes[3] === 0x47;
                            let isPDF = magicBytes[0] === 0x25 && magicBytes[1] === 0x50 && magicBytes[2] === 0x44 && magicBytes[3] === 0x46;
                            
                            // Se for PDF, tentar converter para PNG usando transformação do Cloudinary
                            if (isPDF) {
                                const urlLower = String(assinaturaInfo.assinaturaUrl).toLowerCase();
                                const isCloudinaryUrl = urlLower.includes('cloudinary');
                                
                                if (isCloudinaryUrl) {
                                    // Tentar forçar conversão para PNG usando transformação do Cloudinary
                                    try {
                                        let urlParaConversao = assinaturaInfo.assinaturaUrl;
                                        
                                        // Se já tem transformações, adicionar f=png
                                        // Se não tem, adicionar transformação básica
                                        if (urlParaConversao.includes('/image/upload/')) {
                                            // Já é /image/upload/, adicionar f=png
                                            const separator = urlParaConversao.includes('?') ? '&' : '?';
                                            urlParaConversao = `${urlParaConversao}${separator}f=png&_t=${Date.now()}`;
                                        } else if (urlParaConversao.includes('/raw/upload/')) {
                                            // Converter /raw/upload/ para /image/upload/ e adicionar f=png
                                            urlParaConversao = urlParaConversao.replace('/raw/upload/', '/image/upload/');
                                            const separator = urlParaConversao.includes('?') ? '&' : '?';
                                            urlParaConversao = `${urlParaConversao}${separator}f=png&_t=${Date.now()}`;
                                        } else {
                                            // Tentar adicionar transformação básica
                                            const separator = urlParaConversao.includes('?') ? '&' : '?';
                                            urlParaConversao = `${urlParaConversao}${separator}f=png&_t=${Date.now()}`;
                                        }
                                        
                                        // Tentar buscar a versão PNG
                                        const responsePNG = await fetch(urlParaConversao, fetchOptions);
                                        
                                        if (responsePNG.ok) {
                                            blob = await responsePNG.blob();
                                            arrayBuffer = await blob.arrayBuffer();
                                            uint8Array = new Uint8Array(arrayBuffer);
                                            magicBytes = Array.from(uint8Array.slice(0, 4));
                                            
                                            // Verificar se agora é PNG
                                            isPNG = magicBytes[0] === 0x89 && magicBytes[1] === 0x50 && magicBytes[2] === 0x4E && magicBytes[3] === 0x47;
                                            isPDF = false;
                                            
                                            if (isPNG) {
                                                console.warn(`[gerarNotasClinicasPDF] Assinatura PDF da nota ${assinaturaInfo.notaId} convertida para PNG com sucesso via Cloudinary`);
                                            } else {
                                                // Ainda não é PNG, não podemos usar
                                                throw new Error('Conversão de PDF para PNG não foi bem-sucedida');
                                            }
                                        } else {
                                            throw new Error('Não foi possível converter PDF para PNG');
                                        }
                                    } catch (conversionError) {
                                        // Se a conversão falhar, apenas logar e continuar sem a assinatura desta nota
                                        console.warn(`[gerarNotasClinicasPDF] Não foi possível converter assinatura PDF da nota ${assinaturaInfo.notaId} para PNG. A assinatura não será exibida.`, conversionError);
                                        // Pular esta assinatura e continuar com as outras
                                        continue;
                                    }
                                } else {
                                    // Não é Cloudinary, não podemos converter
                                    console.warn(`[gerarNotasClinicasPDF] A assinatura da nota ${assinaturaInfo.notaId} é um arquivo PDF e não pode ser convertida para PNG. A assinatura não será exibida.`);
                                    // Pular esta assinatura e continuar com as outras
                                    continue;
                                }
                            }
                            
                            // Se o tipo MIME não for imagem, forçar como PNG
                            let blobParaUsar = blob;
                            if (!blob.type || !blob.type.startsWith('image/')) {
                                blobParaUsar = new Blob([blob], { type: 'image/png' });
                            }
                            
                            // Reutilizar o arrayBuffer já obtido anteriormente
                            const uint8ArrayParaPdf = new Uint8Array(arrayBuffer);

                            // Carregar imagem para obter dimensões usando data URL temporária
                            const imgDataUrl = await new Promise((resolve, reject) => {
                                const reader = new FileReader();
                                reader.onload = () => resolve(reader.result);
                                reader.onerror = reject;
                                reader.readAsDataURL(blob);
                            });

                            const img = new Image();
                            await new Promise((resolve, reject) => {
                                img.onload = resolve;
                                img.onerror = reject;
                                img.src = imgDataUrl;
                            });

                            const imgWidth = img.width;
                            const imgHeight = img.height;

                            // Calcular dimensões mantendo proporção (largura máxima: 60mm)
                            const maxWidth = 60; // mm
                            const aspectRatio = imgHeight / imgWidth;
                            let finalImgWidth = maxWidth;
                            let finalImgHeight = maxWidth * aspectRatio;
                            
                            // Limitar altura máxima a 30mm
                            if (finalImgHeight > 30) {
                                finalImgHeight = 30;
                                finalImgWidth = 30 / aspectRatio;
                            }
                            
                            // Converter mm para pontos (1mm = 2.83465 pontos)
                            const widthPt = finalImgWidth * 2.83465;
                            const heightPt = finalImgHeight * 2.83465;
                            
                            // Obter dimensões da página
                            const { width: pageWidthPt, height: pageHeightPt } = pagina.getSize();
                            const marginPt = 20 * 2.83465;
                            
                            // Converter posição Y de mm para pontos (jsPDF usa mm, pdf-lib usa pontos)
                            // Nota: em pdf-lib, Y=0 é na parte inferior da página
                            // yPos armazenado é logo após o texto "Assinatura:", então adicionamos um pequeno offset
                            const offsetY = 2; // mm de espaço entre o texto e a imagem
                            const yPosPt = pageHeightPt - ((assinaturaInfo.yPos + offsetY) * 2.83465) - heightPt;
                            const xPos = marginPt;
                            
                            // Adicionar imagem da assinatura usando Uint8Array
                            const embeddedImg = await pdfDoc.embedPng(uint8ArrayParaPdf);
                            pagina.drawImage(embeddedImg, {
                                x: xPos,
                                y: yPosPt,
                                width: widthPt,
                                height: heightPt
                            });
                        } catch (error) {
                            console.error(`[gerarNotasClinicasPDF] Erro ao adicionar assinatura individual da nota ${assinaturaInfo.notaId}:`, error);
                        }
                    }
                }

                // IMPORTANTE: Não incluir assinatura do perfil do usuário atual
                // Cada nota já tem sua própria assinatura (nota.assinatura_url) que foi salva quando assinada
                // O bloco abaixo foi removido para garantir que apenas as assinaturas salvas nas notas sejam usadas

                // Adicionar rodapé em todas as páginas usando pdf-lib
                const totalPagesFinal = pdfDoc.getPageCount();
                const helveticaFont = await pdfDoc.embedFont('Helvetica');
                
                // Data e hora de geração
                const dataHoraGeracao = new Date();
                const dataFormatada = dataHoraGeracao.toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                });
                const horaFormatada = dataHoraGeracao.toLocaleTimeString('pt-BR', {
                    hour: '2-digit',
                    minute: '2-digit'
                });
                
                for (let i = 0; i < totalPagesFinal; i++) {
                    const page = pdfDoc.getPage(i);
                    const { width } = page.getSize();
                    
                    // Texto do rodapé com número de página e data/hora
                    const textoRodape = `Página ${i + 1} de ${totalPagesFinal} | Gerado em ${dataFormatada} às ${horaFormatada} pela Higeia IA`;
                    
                    // Calcular largura do texto para centralizar
                    const textWidth = helveticaFont.widthOfTextAtSize(textoRodape, 8);
                    const xPos = (width - textWidth) / 2;
                    
                    // Adicionar texto do rodapé centralizado
                    page.drawText(textoRodape, {
                        x: xPos,
                        y: 20,
                        size: 8,
                        color: rgb(0.5, 0.5, 0.5), // Cinza
                        font: helveticaFont,
                    });
                }

                pdfFinal = await pdfDoc.save();
            } catch (error) {
                console.error('[gerarNotasClinicasPDF] Erro ao mesclar documentos PDF:', error);
                // Se houver erro na mesclagem, adicionar rodapé ao PDF base
                try {
                    const pdfDocBase = await PDFDocument.load(pdfBaseBytes);
                    const totalPagesFinal = pdfDocBase.getPageCount();
                    const helveticaFont = await pdfDocBase.embedFont('Helvetica');
                    
                    // Data e hora de geração (fora do loop para eficiência)
                    const dataHoraGeracao = new Date();
                    const dataFormatada = dataHoraGeracao.toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                    });
                    const horaFormatada = dataHoraGeracao.toLocaleTimeString('pt-BR', {
                        hour: '2-digit',
                        minute: '2-digit'
                    });
                    
                    for (let i = 0; i < totalPagesFinal; i++) {
                        const page = pdfDocBase.getPage(i);
                        const { width } = page.getSize();
                        
                        const textoRodape = `Página ${i + 1} de ${totalPagesFinal} | Gerado em ${dataFormatada} às ${horaFormatada} pela Higeia IA`;
                        const textWidth = helveticaFont.widthOfTextAtSize(textoRodape, 8);
                        const xPos = (width - textWidth) / 2;
                        
                        page.drawText(textoRodape, {
                            x: xPos,
                            y: 20,
                            size: 8,
                            color: rgb(0.5, 0.5, 0.5),
                            font: helveticaFont,
                        });
                    }
                    
                    pdfFinal = await pdfDocBase.save();
                } catch (error2) {
                    console.error('[gerarNotasClinicasPDF] Erro ao adicionar rodapé:', error2);
                    pdfFinal = pdfBaseBytes;
                }
            }
        } else {
            // Adicionar rodapé mesmo quando não há documentos ou assinaturas
            const pdfDocBase = await PDFDocument.load(pdfBaseBytes);
            const totalPagesFinal = pdfDocBase.getPageCount();
            const helveticaFont = await pdfDocBase.embedFont('Helvetica');
            
            // Data e hora de geração (fora do loop para eficiência)
            const dataHoraGeracao = new Date();
            const dataFormatada = dataHoraGeracao.toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
            const horaFormatada = dataHoraGeracao.toLocaleTimeString('pt-BR', {
                hour: '2-digit',
                minute: '2-digit'
            });
            
            for (let i = 0; i < totalPagesFinal; i++) {
                const page = pdfDocBase.getPage(i);
                const { width } = page.getSize();
                
                const textoRodape = `Página ${i + 1} de ${totalPagesFinal} | Gerado em ${dataFormatada} às ${horaFormatada} pela Higeia IA`;
                const textWidth = helveticaFont.widthOfTextAtSize(textoRodape, 8);
                const xPos = (width - textWidth) / 2;
                
                page.drawText(textoRodape, {
                    x: xPos,
                    y: 20,
                    size: 8,
                    color: rgb(0.5, 0.5, 0.5),
                    font: helveticaFont,
                });
            }
            
            pdfFinal = await pdfDocBase.save();
        }

        // Fazer download do PDF final
        const blob = new Blob([pdfFinal], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Notas_Clinicas_${paciente.nome_completo?.replace(/\s+/g, '_') || 'Paciente'}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

    } catch (error) {
        console.error('Erro ao gerar PDF das notas clínicas:', error);
        throw new Error('Erro ao gerar o PDF das notas clínicas. Por favor, tente novamente.');
    }
};

